var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from "lit";
import { property, query, state } from "lit/decorators.js";
import { reset } from "../styles.js";
import { uniqueId } from "../utils.js";
import { styles } from "./styles.js";
const ccGridLayout = {
    cols1: "grid-cols-1",
    cols2: "grid-cols-2",
    cols3: "grid-cols-3",
    cols4: "grid-cols-4",
    cols5: "grid-cols-5",
    cols6: "grid-cols-6",
    cols7: "grid-cols-7",
    cols8: "grid-cols-8",
    cols9: "grid-cols-9",
};
// Simple debounce utility
function debounce(func, wait = 200, immediate = false) {
    let timeout;
    return ((...args) => {
        const later = () => {
            timeout = null;
            if (!immediate)
                func(...args);
        };
        const callNow = immediate && !timeout;
        if (timeout)
            clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow)
            func(...args);
    });
}
/**
 * Tabs are used to organize content by grouping similar information on the same page.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/tabs/frameworks/elements)
 *
 * @event {WarpTabsChangeEvent} change - Includes `details.panelId` with the now active tab panel's ID
 */
export class WarpTabs extends LitElement {
    static { this.styles = [reset, styles]; }
    constructor() {
        super();
        this._uniqueId = uniqueId();
        this._activeTabFor = "";
        this._updateSelectionIndicatorDebounced = debounce(this.updateSelectionIndicator.bind(this), 100);
        this._assignSlots = () => {
            [...this.children].forEach((child) => {
                if (!(child instanceof HTMLElement))
                    return;
                // Only set slot if not already set by the consumer.
                if (child.tagName === "W-TAB" && !child.getAttribute("slot"))
                    child.setAttribute("slot", "tabs");
                if (child.tagName === "W-TAB-PANEL" && !child.getAttribute("slot"))
                    child.setAttribute("slot", "panels");
            });
            this.requestUpdate();
        };
        this._handleTabClick = (event) => {
            const newActiveTab = event.tab?.for;
            if (!newActiveTab)
                return;
            if (newActiveTab !== this._activeTabFor) {
                this.active = newActiveTab;
                this._activeTabFor = newActiveTab;
                this.updateSelectionIndicator();
                this.updatePanels();
                this._notifyTabChange();
            }
        };
        this._handleKeyDown = (event) => {
            if (event.altKey ||
                event.ctrlKey ||
                event.shiftKey ||
                !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
                return;
            }
            const tabs = [...this.querySelectorAll("w-tab")];
            const currentIndex = tabs.findIndex((tab) => tab.for === this._activeTabFor);
            if (currentIndex === -1)
                return;
            let nextIndex = currentIndex;
            switch (event.key) {
                case "Home":
                    nextIndex = 0;
                    break;
                case "End":
                    nextIndex = tabs.length - 1;
                    break;
                case "ArrowLeft":
                    nextIndex = Math.max(0, currentIndex - 1);
                    break;
                case "ArrowRight":
                    nextIndex = Math.min(tabs.length - 1, currentIndex + 1);
                    break;
            }
            if (nextIndex !== currentIndex) {
                event.preventDefault();
                const nextTab = tabs[nextIndex];
                const nextTabName = nextTab.for;
                if (nextTabName) {
                    this.active = nextTabName;
                    this._activeTabFor = nextTabName;
                    this.updateSelectionIndicator();
                    this.updatePanels();
                    this._notifyTabChange();
                    // Focus the next tab (updatePanels already set the tabIndex)
                    nextTab.focus();
                }
            }
        };
        this.addEventListener("click", this._handleTabClick);
    }
    connectedCallback() {
        super.connectedCallback();
        // Ensure we show the correct tab on first render if active is set by the user
        if (this.active) {
            this._activeTabFor = this.active;
        }
        this._assignSlots();
        // Set up resize observer for selection indicator updates
        if (typeof ResizeObserver !== "undefined") {
            this._resizeObserver = new ResizeObserver(() => {
                this._updateSelectionIndicatorDebounced();
            });
            this._resizeObserver.observe(this);
        }
        // Fallback for older browsers
        window.addEventListener("resize", this._updateSelectionIndicatorDebounced);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
        window.removeEventListener("resize", this._updateSelectionIndicatorDebounced);
        this.removeEventListener("click", this._handleTabClick);
    }
    firstUpdated() {
        this._assignSlots();
        this._initializeActiveTab();
        this.updateSelectionIndicator();
        this.updatePanels();
    }
    updated(changedProperties) {
        if (changedProperties.has("active") && this.active !== this._activeTabFor) {
            this._activeTabFor = this.active;
            this.updateSelectionIndicator();
            this.updatePanels();
            this._notifyTabChange();
        }
    }
    /** @internal */
    get tabs() {
        const slot = this.shadowRoot?.querySelector('slot[name="tabs"]');
        if (!slot)
            return [];
        const slottedElements = slot.assignedElements({ flatten: true });
        return slottedElements.filter((el) => el.tagName.toLowerCase() === "w-tab");
    }
    /** @internal */
    get activeTab() {
        const activeTabs = this.tabs.filter((tab) => tab.for === this._activeTabFor);
        return activeTabs[0];
    }
    _initializeActiveTab() {
        if (this.activeTab) {
            this._activeTabFor = this.activeTab.getAttribute("for") || "";
        }
        else if (this.tabs.length > 0) {
            this._activeTabFor = this.tabs[0].getAttribute("for") || "";
        }
        if (this._activeTabFor && this._activeTabFor !== this.active) {
            this.active = this._activeTabFor;
        }
    }
    _notifyTabChange() {
        this.dispatchEvent(new CustomEvent("change", {
            detail: { panelId: this._activeTabFor },
            bubbles: true,
            composed: true,
        }));
    }
    updateSelectionIndicator() {
        requestAnimationFrame(() => {
            if (!this.tabList || !this.selectionIndicator)
                return;
            if (!this.activeTab)
                return;
            const tabListRect = this.tabList.getBoundingClientRect();
            const activeTabRect = this.activeTab.getBoundingClientRect();
            const left = activeTabRect.left - tabListRect.left;
            const width = activeTabRect.width;
            this.selectionIndicator.style.left = `${left}px`;
            this.selectionIndicator.style.width = `${width}px`;
        });
    }
    updatePanels() {
        // Update tab active states using non-reflecting properties to reduce risk of hydration mismatch
        const tabs = [...this.querySelectorAll("w-tab")];
        tabs.forEach((tab, index) => {
            if (!tab.id) {
                tab.id = `w-tab-${this._uniqueId}-${index}`;
            }
            const isActive = tab.for === this._activeTabFor;
            // Use reflecting properties so assitsive technologies have what they need in the light DOM
            tab.tabIndex = isActive ? 0 : -1;
            // Use non-reflecting property to reduce DOM changes during hydration
            tab._parentAriaSelected = isActive ? "true" : "false";
        });
        // Update tab panels visibility using non-reflecting properties to reduce risk of hydration mismatch
        const panels = [...this.querySelectorAll("w-tab-panel")];
        panels.forEach((panel) => {
            const controller = tabs.find((tab) => tab.for === panel.id);
            if (controller) {
                // Use non-reflecting property to reduce DOM changes during hydration
                panel._parentAriaLabelledBy = controller.id;
            }
            // Use non-reflecting property to control visibility
            panel._parentActive = panel.id === this._activeTabFor;
        });
    }
    get _tabCount() {
        return this.querySelectorAll("w-tab").length;
    }
    get _gridClass() {
        const count = Math.min(this._tabCount, 9);
        return (ccGridLayout[`cols${count}`] ||
            ccGridLayout.cols1);
    }
    render() {
        return html `
			<div part="base">
				<div
					role="tablist"
					part="tablist"
					class="${this._gridClass}"
					@keydown="${this._handleKeyDown}"
				>
					<slot name="tabs" @slotchange="${this._assignSlots}"></slot>
					<span part="selection" data-testid="selection-indicator"></span>
				</div>
			</div>
			<slot name="panels" @slotchange="${this._assignSlots}"></slot>
			<slot @slotchange="${this._assignSlots}"></slot>
		`;
    }
}
__decorate([
    property({ reflect: true })
], WarpTabs.prototype, "active", void 0);
__decorate([
    query('[role="tablist"]')
], WarpTabs.prototype, "tabList", void 0);
__decorate([
    query('[part="selection"]')
], WarpTabs.prototype, "selectionIndicator", void 0);
__decorate([
    state()
], WarpTabs.prototype, "_activeTabFor", void 0);
if (!customElements.get("w-tabs")) {
    customElements.define("w-tabs", WarpTabs);
}
//# sourceMappingURL=tabs.js.map